import { useState, useEffect } from "react";
import { X, ShoppingCart, AlertCircle, Calendar, Package, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "../../lib/api";
import { errorMessage, getTodayDateString } from "../../lib/format";
import { canViewClientPhone } from "../../lib/permissions";

const PRIORITIES = ["Low", "Medium", "High", "Urgent"];
const COMMON_UNITS = ["units", "pcs", "kg", "meters", "boxes", "sets", "rolls", "sqft"];

const createDefaultProduct = (productName = "", orderId = "", description = "", quantity = 1, unit = "units", notes = "") => ({
  id: `prod_${Date.now()}_${Math.random().toString(36).substr(2, 5)}`,
  order_id: orderId || "",
  product_name: productName || "",
  description: description || "",
  quantity: quantity || 1,
  unit: unit || "units",
  priority: "Medium",
  required_by: "",
  additional_notes: notes || "",
});

export default function RequestPriceModal({
  isOpen,
  onClose,
  lead,
  client,
  selectedOrders = [],
  currentUser,
  onSuccess,
}) {
  const [products, setProducts] = useState([createDefaultProduct()]);
  const [assignedToId, setAssignedToId] = useState("");
  const [procurementUsers, setProcurementUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState("");

  const targetEntity = client || lead;

  // Initialize form when lead, client, or selectedOrders changes
  useEffect(() => {
    if (targetEntity && isOpen) {
      if (selectedOrders && selectedOrders.length > 0) {
        const seenOrderIds = new Set();
        const mapped = [];
        selectedOrders.forEach((o, idx) => {
          const ordId = o.order_id || "";
          if (ordId && seenOrderIds.has(ordId)) return;
          if (ordId) seenOrderIds.add(ordId);

          mapped.push({
            id: `prod_${ordId || idx}_${Date.now()}_${Math.random().toString(36).substr(2, 4)}`,
            order_id: ordId,
            product_name: o.product || o.description || "Custom Item",
            description: o.description || "",
            quantity: parseFloat(o.quantity) || 1,
            unit: o.unit || "units",
            priority: o.priority === "Urgent" || o.priority === "High" ? o.priority : "Medium",
            required_by: o.required_delivery_date || "",
            additional_notes: o.description
              ? (o.notes ? `${o.description} — ${o.notes}` : o.description)
              : (o.notes || ""),
          });
        });
        setProducts(mapped.length > 0 ? mapped : [createDefaultProduct(targetEntity.product_interest || "")]);
      } else {
        setProducts([createDefaultProduct(targetEntity.product_interest || "")]);
      }
      setAssignedToId("");
      setFormError("");
    }
  }, [targetEntity, isOpen, selectedOrders]);

  // Fetch Procurement users for assignment dropdown
  useEffect(() => {
    if (!isOpen) return;
    setLoadingUsers(true);
    setAssignedToId(""); // Default to Auto-assign (Procurement Team)
    api("get", "/users?department=Procurement&active_only=true")
      .then((res) => {
        if (Array.isArray(res.data)) {
          // Strictly show ALL active, non-deleted users belonging to Procurement department
          const list = res.data.filter((u) => {
            if (u.is_active === false || u.is_deleted === true) return false;
            const depts = Array.isArray(u.departments) && u.departments.length > 0
              ? u.departments.map((d) => (d || "").toLowerCase().trim())
              : [(u.department || "").toLowerCase().trim()];
            const role = (u.role || "").toLowerCase().trim();
            return depts.includes("procurement") || role === "procurement";
          });
          list.sort((a, b) => (a.name || "").localeCompare(b.name || ""));
          setProcurementUsers(list);
        }
      })
      .catch((err) => {
        console.error("Failed to load procurement specialists:", err);
      })
      .finally(() => setLoadingUsers(false));
  }, [isOpen]);

  if (!isOpen || !targetEntity) return null;

  const handleAddProduct = () => {
    setProducts((prev) => [...prev, createDefaultProduct()]);
  };

  const handleRemoveProduct = (index) => {
    if (products.length <= 1) return;
    setProducts((prev) => prev.filter((_, i) => i !== index));
  };

  const handleProductChange = (index, field, value) => {
    setProducts((prev) => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };
      return updated;
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormError("");

    // Validate all products
    const seenKeys = new Set();
    for (let i = 0; i < products.length; i++) {
      const p = products[i];
      if (!p.product_name || !p.product_name.trim()) {
        const err = `Product #${i + 1}: Product name or specification is required.`;
        setFormError(err);
        return;
      }
      if (!p.quantity || Number(p.quantity) <= 0) {
        const err = `Product #${i + 1} (${p.product_name}): Quantity must be greater than 0.`;
        setFormError(err);
        return;
      }

      // Prevent duplicate submissions of the same products within the same request
      const dedupKey = p.order_id
        ? `order:${p.order_id}`
        : `prod:${p.product_name.trim().toLowerCase()}:${p.quantity}:${(p.unit || "").trim().toLowerCase()}`;
      if (seenKeys.has(dedupKey)) {
        const err = `Duplicate product detected: "${p.product_name}" (Row #${i + 1}). Please remove duplicates before submitting.`;
        setFormError(err);
        return;
      }
      seenKeys.add(dedupKey);
    }

    setSubmitting(true);
    try {
      const itemsPayload = products.map((p) => ({
        order_id: (p.order_id || "").trim(),
        product_name: p.product_name.trim(),
        description: (p.description || "").trim(),
        quantity: parseFloat(p.quantity) || 1.0,
        unit: (p.unit || "units").trim(),
        priority: p.priority || "Medium",
        required_by: p.required_by || "",
        additional_notes: (p.additional_notes || "").trim(),
      }));

      const payload = {
        items: itemsPayload,
        // Also provide top-level fallback for backward compatibility
        product_name: itemsPayload[0].product_name,
        quantity: itemsPayload[0].quantity,
        unit: itemsPayload[0].unit,
        priority: itemsPayload[0].priority,
        required_by: itemsPayload[0].required_by,
        additional_notes: itemsPayload[0].additional_notes,
        assigned_to_id: assignedToId || undefined,
      };

      if (client) {
        payload.client_id = client.client_id;
      }
      if (lead) {
        payload.lead_id = lead.lead_id;
      }

      const res = await api("post", "/procurement", payload);
      const prodCount = itemsPayload.length;
      toast.success(
        prodCount > 1
          ? `Price request submitted (${res.data.request_id}) for ${prodCount} products`
          : `Price request submitted (${res.data.request_id}) for ${itemsPayload[0].product_name}`
      );
      if (onSuccess) onSuccess(res.data);
      onClose();
    } catch (err) {
      const msg = errorMessage(err, "Failed to submit price request");
      setFormError(msg);
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="modal-backdrop"
      style={{ zIndex: 10000 }}
      data-testid="request-price-modal-backdrop"
    >
      <div
        className="modal"
        style={{ maxWidth: 640, width: "95%", maxHeight: "90vh", display: "flex", flexDirection: "column" }}
        data-testid="request-price-modal"
      >
        {/* Modal Head */}
        <div className="modal-head" style={{ flexShrink: 0 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <div
              style={{
                width: 36,
                height: 36,
                borderRadius: 8,
                background: "#f1f5f9",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: "var(--navy)",
              }}
            >
              <ShoppingCart size={18} />
            </div>
            <div>
              <h2 style={{ margin: 0, fontSize: 17, color: "var(--navy)" }}>
                Request Product Price {products.length > 1 && `(${products.length} Products)`}
              </h2>
              <p style={{ margin: "2px 0 0", fontSize: 12, color: "var(--muted)" }}>
                Procurement team will discover vendor pricing and update this {client ? "client" : "lead"}.
              </p>
            </div>
          </div>
          <button
            type="button"
            className="icon-btn"
            onClick={onClose}
            data-testid="close-request-price-modal"
          >
            <X size={18} />
          </button>
        </div>

        {/* Scrollable Body */}
        <div style={{ flex: 1, overflowY: "auto", paddingRight: 2 }}>
          {/* Client / Lead Context Pill */}
          <div
            style={{
              background: "#f8fafc",
              border: "1px solid var(--border)",
              borderRadius: 6,
              padding: "8px 12px",
              marginBottom: 16,
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              fontSize: 12,
            }}
          >
            <div>
              <span style={{ color: "var(--muted)" }}>{client ? "Client:" : "Lead:"}</span>{" "}
              <strong style={{ color: "var(--navy)" }}>{targetEntity.company_name}</strong>{" "}
              <span style={{ color: "var(--muted)", fontSize: 11 }}>
                ({client ? client.client_id : lead.lead_id})
              </span>
            </div>
            {targetEntity.contact_person && (
              <div style={{ color: "var(--muted)", fontSize: 11 }}>
                Contact: <span style={{ color: "var(--navy)" }}>{targetEntity.contact_person}</span>
                {canViewClientPhone(currentUser) && targetEntity.phone && ` · ${targetEntity.phone}`}
              </div>
            )}
          </div>

          {formError && (
            <div
              style={{
                background: "#fef2f2",
                border: "1px solid #fca5a5",
                borderRadius: 6,
                padding: "8px 12px",
                marginBottom: 16,
                fontSize: 12,
                color: "#b91c1c",
                display: "flex",
                alignItems: "center",
                gap: 8,
              }}
            >
              <AlertCircle size={15} style={{ flexShrink: 0 }} />
              <span>{formError}</span>
            </div>
          )}

          <form id="request-price-form" onSubmit={handleSubmit}>
            {/* Products List */}
            <div style={{ display: "flex", flexDirection: "column", gap: 14, marginBottom: 16 }}>
              {products.map((prod, index) => (
                <div
                  key={prod.id}
                  style={{
                    background: "#ffffff",
                    border: "1.5px solid #e2e8f0",
                    borderRadius: 8,
                    padding: 14,
                    boxShadow: "0 1px 3px rgba(0,0,0,0.02)",
                  }}
                  data-testid={`request-price-product-card-${index}`}
                >
                  {/* Product Card Header */}
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      marginBottom: 12,
                      paddingBottom: 8,
                      borderBottom: "1px solid #f1f5f9",
                    }}
                  >
                    <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                      <span
                        style={{
                          background: "#eff6ff",
                          color: "#2563eb",
                          fontSize: 11,
                          fontWeight: 700,
                          padding: "2px 8px",
                          borderRadius: 4,
                          border: "1px solid #bfdbfe",
                        }}
                      >
                        Product #{index + 1}
                      </span>
                      {prod.order_id && (
                        <span
                          style={{
                            background: "#e0e7ff",
                            color: "#3730a3",
                            fontSize: 10.5,
                            fontWeight: 700,
                            padding: "2px 7px",
                            borderRadius: 4,
                            fontFamily: "monospace",
                          }}
                        >
                          {prod.order_id}
                        </span>
                      )}
                      {prod.product_name && (
                        <span style={{ fontSize: 12, fontWeight: 600, color: "#1e293b" }}>
                          {prod.product_name}
                        </span>
                      )}
                    </div>
                    {products.length > 1 && (
                      <button
                        type="button"
                        onClick={() => handleRemoveProduct(index)}
                        style={{
                          background: "none",
                          border: "none",
                          color: "#dc2626",
                          cursor: "pointer",
                          padding: "2px 6px",
                          fontSize: 11.5,
                          fontWeight: 500,
                          display: "inline-flex",
                          alignItems: "center",
                          gap: 4,
                        }}
                        title="Remove product"
                        data-testid={`remove-product-btn-${index}`}
                      >
                        <Trash2 size={13} /> Remove
                      </button>
                    )}
                  </div>

                  {/* Product Name & Description */}
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginBottom: 10 }}>
                    <div className="dispatch-field" style={{ margin: 0 }}>
                      <label className="dispatch-label" style={{ fontWeight: 600, fontSize: 11.5 }}>
                        Product Name <span style={{ color: "var(--red)" }}>*</span>
                      </label>
                      <input
                        type="text"
                        className="dispatch-control"
                        placeholder="e.g., Packaging Boxes, Visiting Cards..."
                        value={prod.product_name}
                        onChange={(e) => handleProductChange(index, "product_name", e.target.value)}
                        required
                        data-testid={`request-price-product-name-${index}`}
                        autoFocus={index === 0}
                      />
                    </div>
                    <div className="dispatch-field" style={{ margin: 0 }}>
                      <label className="dispatch-label" style={{ fontWeight: 600, fontSize: 11.5 }}>
                        Description
                      </label>
                      <input
                        type="text"
                        className="dispatch-control"
                        placeholder="e.g., 3-ply heavy duty box 12x10x8"
                        value={prod.description || ""}
                        onChange={(e) => handleProductChange(index, "description", e.target.value)}
                        data-testid={`request-price-description-${index}`}
                      />
                    </div>
                  </div>

                  {/* Quantity & Unit Row */}
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginBottom: 10 }}>
                    <div className="dispatch-field" style={{ margin: 0 }}>
                      <label className="dispatch-label" style={{ fontWeight: 600, fontSize: 11.5 }}>
                        Required Quantity <span style={{ color: "var(--red)" }}>*</span>
                      </label>
                      <input
                        type="number"
                        min="0.01"
                        step="any"
                        className="dispatch-control"
                        placeholder="e.g. 500"
                        value={prod.quantity}
                        onChange={(e) => handleProductChange(index, "quantity", e.target.value)}
                        required
                        data-testid={`request-price-quantity-${index}`}
                      />
                    </div>
                    <div className="dispatch-field" style={{ margin: 0 }}>
                      <label className="dispatch-label" style={{ fontWeight: 600, fontSize: 11.5 }}>
                        Unit of Measurement
                      </label>
                      <input
                        type="text"
                        list="units-list"
                        className="dispatch-control"
                        placeholder="boxes / pcs / units / kg"
                        value={prod.unit}
                        onChange={(e) => handleProductChange(index, "unit", e.target.value)}
                        data-testid={`request-price-unit-${index}`}
                      />
                    </div>
                  </div>

                  {/* Priority & Required-By Row */}
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginBottom: 10 }}>
                    <div className="dispatch-field" style={{ margin: 0 }}>
                      <label className="dispatch-label" style={{ fontWeight: 600, fontSize: 11.5 }}>
                        Priority
                      </label>
                      <select
                        className="dispatch-control"
                        value={prod.priority}
                        onChange={(e) => handleProductChange(index, "priority", e.target.value)}
                        data-testid={`request-price-priority-${index}`}
                      >
                        {PRIORITIES.map((p) => (
                          <option key={p} value={p}>
                            {p === "Urgent" ? "🔥 Urgent" : p}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="dispatch-field" style={{ margin: 0 }}>
                      <label className="dispatch-label" style={{ fontWeight: 600, fontSize: 11.5 }}>
                        Required-By Date / Time
                      </label>
                      <input
                        type="date"
                        className="dispatch-control"
                        value={prod.required_by}
                        min={getTodayDateString()}
                        onChange={(e) => handleProductChange(index, "required_by", e.target.value)}
                        data-testid={`request-price-required-by-${index}`}
                      />
                    </div>
                  </div>

                  {/* Additional Notes */}
                  <div className="dispatch-field" style={{ margin: 0 }}>
                    <label className="dispatch-label" style={{ fontWeight: 600, fontSize: 11.5 }}>
                      Additional Specifications / Remarks (Optional)
                    </label>
                    <input
                      type="text"
                      className="dispatch-control"
                      placeholder="GSM, thickness, material specifications, tolerances, target price..."
                      value={prod.additional_notes}
                      onChange={(e) => handleProductChange(index, "additional_notes", e.target.value)}
                      data-testid={`request-price-notes-${index}`}
                    />
                  </div>
                </div>
              ))}
            </div>

            {/* Add Another Product Button */}
            <div style={{ marginBottom: 18 }}>
              <button
                type="button"
                className="outline-btn"
                onClick={handleAddProduct}
                data-testid="request-price-add-product-btn"
                style={{
                  width: "100%",
                  padding: "9px 14px",
                  border: "1.5px dashed #cbd5e1",
                  background: "#f8fafc",
                  color: "#2563eb",
                  fontWeight: 600,
                  fontSize: 12.5,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 6,
                  borderRadius: 6,
                  cursor: "pointer",
                }}
              >
                <Plus size={15} /> + Add Another Product
              </button>
            </div>

            {/* Datalist for unit suggestions */}
            <datalist id="units-list">
              {COMMON_UNITS.map((u) => (
                <option key={u} value={u} />
              ))}
            </datalist>

            {/* Request Assignment Section */}
            <div
              style={{
                background: "#f8fafc",
                border: "1px solid var(--border)",
                borderRadius: 8,
                padding: "12px 14px",
                marginBottom: 16,
              }}
            >
              <div className="dispatch-field" style={{ margin: 0 }}>
                <label className="dispatch-label" style={{ fontWeight: 600, fontSize: 12 }}>
                  Assign to Procurement Specialist
                </label>
                <select
                  className="dispatch-control"
                  value={assignedToId}
                  onChange={(e) => setAssignedToId(e.target.value)}
                  data-testid="request-price-assignee-select"
                >
                  <option value="">Auto-assign (Procurement Team)</option>
                  {procurementUsers.map((u) => {
                    const extra = [u.email, u.company].filter(Boolean).join(" · ");
                    return (
                      <option key={u.user_id} value={u.user_id}>
                        {u.name}{extra ? ` (${extra})` : ""}
                      </option>
                    );
                  })}
                </select>
                <div style={{ fontSize: 11, color: "var(--muted)", marginTop: 4 }}>
                  Select a Procurement specialist or let the system auto-assign to the team.
                </div>
              </div>
            </div>
          </form>
        </div>

        {/* Modal Actions Fixed Footer */}
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            paddingTop: 12,
            borderTop: "1px solid var(--border)",
            flexShrink: 0,
            marginTop: 4,
          }}
        >
          <div style={{ fontSize: 12, color: "#64748b" }}>
            Total: <b>{products.length}</b> {products.length === 1 ? "product" : "products"}
          </div>
          <div style={{ display: "flex", gap: 10 }}>
            <button
              type="button"
              className="outline-btn"
              onClick={onClose}
              disabled={submitting}
              data-testid="request-price-cancel-button"
            >
              Cancel
            </button>
            <button
              type="submit"
              form="request-price-form"
              className="primary-btn"
              disabled={submitting}
              data-testid="request-price-submit-button"
              style={{ display: "inline-flex", alignItems: "center", gap: 6 }}
            >
              <ShoppingCart size={15} />
              {submitting ? "Submitting..." : "Send Price Request"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
